import { type FC, useState, useCallback, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Save,
  RotateCcw,
  History,
  Building2,
  MapPin,
  TrendingUp,
  DollarSign,
  Clock,
  BarChart3,
  Calendar,
  CheckCircle,
  AlertTriangle,
  FileText,
  UploadCloud,
  Trash2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Loader2,
  Plus,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Label } from '../../components/ui/Label'
import { Textarea } from '../../components/ui/Textarea'
import { RadioGroup, RadioGroupItem } from '../../components/ui/RadioGroup'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../components/ui/AlertDialog'
import { DialogHeaderWithIcon } from '../../components/ui/DialogHeaderWithIcon'
import { SponsorMonogram } from '../../components/marketplace/SponsorMonogram'
import { ComplianceFooter } from '../../components/marketplace/ComplianceFooter'
import {
  adminApi,
  type MarketplaceProperty,
  type CreateMarketplacePropertyData,
  type MarketplacePropertyVersion,
} from '../../services/api/admin'
import { cn } from '../../lib/utils'
import { assetClassBadgeClass } from '../../lib/marketplaceFormatters'
import { computePropertyDiff } from '../../lib/marketplaceDiff'

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
const MAX_FILE_BYTES = 5 * 1024 * 1024
const AUTOSAVE_DEBOUNCE_MS = 30_000

const DST_ASSET_CLASSES = [
  { value: 'multifamily', label: 'Multifamily' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'office', label: 'Office' },
  { value: 'retail', label: 'Retail' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'self-storage', label: 'Self-Storage' },
]

const DISTRIBUTION_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
]

const editorSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  orgId: z.string().min(1, 'Org ID is required'),
  description: z.string().optional(),
  propertyType: z.string().optional(),
  addressStreet: z.string().optional(),
  addressCity: z.string().optional(),
  addressState: z.string().max(2, 'Use 2-letter state code').optional(),
  addressZip: z.string().optional(),
  askingPriceDollars: z.string().optional(),
  sponsor: z.string().optional(),
  dstAssetClass: z.string().optional(),
  dstMinInvestmentDollars: z.string().optional(),
  dstExpectedReturnMin: z.string().optional(),
  dstExpectedReturnMax: z.string().optional(),
  dstHoldPeriodMonths: z.string().optional(),
  dstDistributionFrequency: z.enum(['monthly', 'quarterly', 'annually']).optional(),
  dstOfferingSizeDollars: z.string().optional(),
  qualifiedStatus: z.enum(['qualified', 'pending_review', 'unqualified']).optional(),
  capRate: z.string().optional(),
  noi: z.string().optional(),
  subProperties: z.array(z.object({
    name: z.string().min(1),
    address: z.string(),
    value: z.coerce.number().nonnegative(),
    imageUrl: z.string().url().nullable().or(z.literal('')).optional(),
  })).default([]),
})

type EditorFormValues = z.infer<typeof editorSchema>

function parseDollarsAsCents(value: string): number | undefined {
  const numeric = parseFloat(value.replace(/[^0-9.]/g, ''))
  if (isNaN(numeric)) return undefined
  return Math.round(numeric * 100)
}

function centsToDisplayDollars(cents: number | null | undefined): string {
  if (cents == null) return ''
  return String(cents / 100)
}

function formatCentsCompact(cents: number | null): string {
  if (cents == null) return '—'
  const dollars = cents / 100
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(0)}K`
  return `$${dollars.toLocaleString('en-US')}`
}

function formatHoldPeriod(months: number | null): string {
  if (months == null) return '—'
  const years = months / 12
  return `${years % 1 === 0 ? years.toFixed(0) : years.toFixed(1)} yr${years !== 1 ? 's' : ''}`
}

function formatReturnRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return '—'
  if (min == null) return `up to ${max}%`
  if (max == null) return `${min}%+`
  if (min === max) return `${min}%`
  return `${min}–${max}%`
}

function formatDistributionFrequency(freq: MarketplaceProperty['dstDistributionFrequency']): string {
  if (freq == null) return '—'
  return freq.charAt(0).toUpperCase() + freq.slice(1)
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (diffSeconds < 60) return 'just now'
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`
  return `${Math.floor(diffSeconds / 86400)}d ago`
}

function storageKey(id: string): string {
  return `marketplace_editor_draft_${id}`
}

function propertyToFormValues(property: MarketplaceProperty): EditorFormValues {
  return {
    title: property.title,
    orgId: property.orgId,
    description: property.description ?? '',
    propertyType: property.propertyType ?? '',
    addressStreet: property.addressStreet ?? '',
    addressCity: property.addressCity ?? '',
    addressState: property.addressState ?? '',
    addressZip: property.addressZip ?? '',
    askingPriceDollars: centsToDisplayDollars(property.askingPriceCents),
    sponsor: property.sponsor ?? '',
    dstAssetClass: property.dstAssetClass ?? '',
    dstMinInvestmentDollars: centsToDisplayDollars(property.dstMinInvestmentCents),
    dstExpectedReturnMin: property.dstExpectedReturnMin != null ? String(property.dstExpectedReturnMin) : '',
    dstExpectedReturnMax: property.dstExpectedReturnMax != null ? String(property.dstExpectedReturnMax) : '',
    dstHoldPeriodMonths: property.dstHoldPeriodMonths != null ? String(property.dstHoldPeriodMonths) : '',
    dstDistributionFrequency: property.dstDistributionFrequency ?? undefined,
    dstOfferingSizeDollars: centsToDisplayDollars(property.dstOfferingSizeCents),
    qualifiedStatus: property.qualifiedStatus ?? 'pending_review',
    capRate: property.capRate != null ? String(property.capRate) : '',
    noi: property.noi != null ? String(property.noi) : '',
    subProperties: property.subProperties ?? [],
  }
}

function buildPayload(
  values: EditorFormValues,
  images: string[],
  published: boolean
): Partial<CreateMarketplacePropertyData> {
  return {
    orgId: values.orgId,
    title: values.title,
    description: values.description,
    propertyType: values.propertyType,
    addressStreet: values.addressStreet,
    addressCity: values.addressCity,
    addressState: values.addressState,
    addressZip: values.addressZip,
    askingPriceCents: parseDollarsAsCents(values.askingPriceDollars ?? ''),
    sponsor: values.sponsor,
    dstAssetClass: values.dstAssetClass,
    dstMinInvestmentCents: parseDollarsAsCents(values.dstMinInvestmentDollars ?? ''),
    dstExpectedReturnMin: values.dstExpectedReturnMin ? Number(values.dstExpectedReturnMin) : undefined,
    dstExpectedReturnMax: values.dstExpectedReturnMax ? Number(values.dstExpectedReturnMax) : undefined,
    dstHoldPeriodMonths: values.dstHoldPeriodMonths ? Number(values.dstHoldPeriodMonths) : undefined,
    dstDistributionFrequency: values.dstDistributionFrequency,
    dstOfferingSizeCents: parseDollarsAsCents(values.dstOfferingSizeDollars ?? ''),
    qualifiedStatus: values.qualifiedStatus ?? 'pending_review',
    capRate: values.capRate ? Number(values.capRate) : undefined,
    noi: values.noi ? Number(values.noi) : undefined,
    images,
    published,
    subProperties: values.subProperties.map((subProperty) => ({
      ...subProperty,
      imageUrl: subProperty.imageUrl === '' ? null : (subProperty.imageUrl ?? null),
    })),
  }
}

interface RailStatProps {
  icon: React.ReactNode
  label: string
  value: string
  highlight?: boolean
}

function RailStat({ icon, label, value, highlight }: RailStatProps) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        {label}
      </div>
      <span className={cn(
        'text-sm font-semibold',
        highlight ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
      )}>
        {value}
      </span>
    </div>
  )
}

interface EditableFieldProps {
  label: string
  children: React.ReactNode
  preview?: string
}

function EditableField({ label, children, preview }: EditableFieldProps) {
  return (
    <div className="group relative">
      <Label className="text-xs text-muted-foreground uppercase tracking-wide mb-1 block">
        {label}
      </Label>
      {children}
      {preview && (
        <p className="text-xs text-muted-foreground mt-1">{preview}</p>
      )}
    </div>
  )
}

interface ImageGalleryProps {
  propertyId: string
  images: string[]
  onImagesChange: (images: string[]) => void
}

const ImageGallery: FC<ImageGalleryProps> = ({ propertyId, images, onImagesChange }) => {
  const [uploading, setUploading] = useState(false)
  const [pendingDeleteUrl, setPendingDeleteUrl] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) return 'Only PNG, JPG, WebP, and GIF images are allowed.'
    if (file.size > MAX_FILE_BYTES) return 'File must be 5MB or smaller.'
    return null
  }

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return
      const validationError = validateFile(file)
      if (validationError) {
        toast.error(validationError)
        return
      }
      try {
        setUploading(true)
        const result = await adminApi.uploadMarketplaceImage(propertyId, file)
        onImagesChange(result.images)
        toast.success('Image uploaded')
      } catch (error) {
        const status = (error as { status?: number }).status
        if (status === 415) {
          toast.error('Unsupported file type. Use PNG, JPG, WebP, or GIF.')
        } else if (status === 413) {
          toast.error('File too large. Maximum size is 5MB.')
        } else if (status === 503) {
          toast.error('Storage unavailable. Please try again later.')
        } else {
          toast.error(error instanceof Error ? error.message : 'Upload failed')
        }
      } finally {
        setUploading(false)
      }
    },
    [propertyId, onImagesChange]
  )

  const handleDropRejected = useCallback(() => {
    toast.error('Only PNG, JPG, WebP, and GIF images are allowed.')
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    accept: { 'image/png': [], 'image/jpeg': [], 'image/webp': [], 'image/gif': [] },
    maxSize: MAX_FILE_BYTES,
    multiple: false,
    disabled: uploading,
  })

  const moveImage = (fromIndex: number, toIndex: number) => {
    const next = [...images]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    onImagesChange(next)
  }

  const confirmDelete = async () => {
    if (!pendingDeleteUrl) return
    try {
      setDeleting(true)
      const result = await adminApi.deleteMarketplaceImage(propertyId, pendingDeleteUrl)
      onImagesChange(result.images)
      toast.success('Image removed')
    } catch {
      toast.error('Failed to delete image')
    } finally {
      setDeleting(false)
      setPendingDeleteUrl(null)
    }
  }

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        role="button"
        aria-label={`Upload image. ${images.length} image${images.length !== 1 ? 's' : ''} uploaded.`}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 rounded-xl',
          'border-2 border-dashed p-8 cursor-pointer transition-all duration-200',
          isDragActive
            ? 'border-primary bg-primary/5 scale-[1.01]'
            : 'border-border hover:border-primary hover:bg-primary/5',
          uploading ? 'pointer-events-none opacity-60' : ''
        )}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <LoadingSpinner size="md" variant="icon" />
        ) : (
          <UploadCloud className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        )}
        <p className="text-sm font-medium text-foreground">
          {isDragActive ? 'Drop image here' : uploading ? 'Uploading…' : 'Drop image or click to upload'}
        </p>
        <p className="text-xs text-muted-foreground">PNG, JPG, WebP, GIF · max 5MB</p>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((url, index) => (
            <div
              key={url}
              className="relative rounded-lg overflow-hidden border border-border aspect-video bg-muted"
            >
              <img
                src={url}
                alt={`Property image ${index + 1}`}
                className="object-cover w-full h-full"
              />
              <span className="absolute top-1 left-1 bg-black/60 text-white text-xs font-medium px-1.5 py-0.5 rounded">
                {index === 0 ? 'Hero' : index + 1}
              </span>
              <div className="absolute top-1 right-1 flex flex-col gap-0.5">
                <button
                  type="button"
                  aria-label={`Move image ${index + 1} up`}
                  disabled={index === 0}
                  onClick={() => moveImage(index, index - 1)}
                  className="bg-black/60 hover:bg-black/80 disabled:opacity-30 text-white rounded p-0.5 transition-colors"
                >
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Move image ${index + 1} down`}
                  disabled={index === images.length - 1}
                  onClick={() => moveImage(index, index + 1)}
                  className="bg-black/60 hover:bg-black/80 disabled:opacity-30 text-white rounded p-0.5 transition-colors"
                >
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
              <button
                type="button"
                aria-label={`Delete image ${index + 1}`}
                onClick={() => setPendingDeleteUrl(url)}
                className="absolute bottom-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded p-0.5 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog
        open={pendingDeleteUrl !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteUrl(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={Trash2}
              iconColor="rose"
              title="Delete image?"
              description="This image will be permanently removed from the property listing."
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} onClick={() => setPendingDeleteUrl(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface VersionHistorySheetProps {
  propertyId: string
  currentProperty: MarketplaceProperty
  open: boolean
  onClose: () => void
  onRestored: () => void
}

interface FieldDiffRowProps {
  label: string
  from: string
  to: string
  longText: boolean
}

const FieldDiffRow: FC<FieldDiffRowProps> = ({ label, from, to, longText }) => {
  const [expanded, setExpanded] = useState(false)
  const fromDisplay = longText && !expanded ? `${from.slice(0, 60)}…` : from
  const toDisplay = longText && !expanded ? `${to.slice(0, 60)}…` : to
  return (
    <div className="text-xs py-1 border-b border-border/50 last:border-0">
      <span className="font-medium text-foreground">{label}:</span>{' '}
      <span className="text-muted-foreground line-through">{fromDisplay}</span>
      {' '}
      <span className="text-foreground font-medium">{toDisplay}</span>
      {longText && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="ml-1.5 text-primary hover:underline focus:outline-none"
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse diff for ${label}` : `Expand diff for ${label}`}
        >
          {expanded ? 'Show less' : 'Show full diff'}
        </button>
      )}
    </div>
  )
}

const VersionHistorySheet: FC<VersionHistorySheetProps> = ({
  propertyId,
  currentProperty,
  open,
  onClose,
  onRestored,
}) => {
  const queryClient = useQueryClient()
  const [pendingRestoreVersion, setPendingRestoreVersion] = useState<number | null>(null)
  const [restoring, setRestoring] = useState(false)

  const { data: versions, isLoading, isError, refetch } = useQuery({
    queryKey: ['marketplace-property-versions', propertyId],
    queryFn: () => adminApi.getMarketplacePropertyVersions(propertyId),
    enabled: open,
  })

  const handleRestore = async () => {
    if (pendingRestoreVersion == null) return
    try {
      setRestoring(true)
      await adminApi.restoreMarketplacePropertyVersion(propertyId, pendingRestoreVersion)
      toast.success(`Restored to version ${pendingRestoreVersion}`)
      queryClient.invalidateQueries({ queryKey: ['marketplace-property-versions', propertyId] })
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-property', propertyId] })
      onRestored()
      onClose()
    } catch {
      toast.error('Failed to restore version')
    } finally {
      setRestoring(false)
      setPendingRestoreVersion(null)
    }
  }

  if (!open) return null

  const sortedVersions = versions
    ? [...versions].sort(
        (a: MarketplacePropertyVersion, b: MarketplacePropertyVersion) =>
          b.versionNumber - a.versionNumber
      )
    : []

  return (
    <div
      className={cn(
        'fixed inset-y-0 right-0 z-50 w-full sm:w-[28rem] bg-background border-l border-border',
        'flex flex-col shadow-2xl transition-transform duration-300',
        open ? 'translate-x-0' : 'translate-x-full'
      )}
      role="dialog"
      aria-label="Version history"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" aria-hidden="true" />
          <h2 className="text-base font-semibold text-foreground">Version history</h2>
        </div>
        <button
          type="button"
          aria-label="Close version history"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
        >
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="md" />
          </div>
        )}
        {isError && (
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
            <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
            <p className="text-sm">Failed to load version history.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
          </div>
        )}
        {!isLoading && !isError && (!versions || versions.length === 0) && (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <History className="h-8 w-8 opacity-40" aria-hidden="true" />
            <p className="text-sm">No version history yet.</p>
          </div>
        )}
        {!isLoading && !isError && sortedVersions.length > 0 && (
          <div className="space-y-3">
            {sortedVersions.map((version: MarketplacePropertyVersion, index: number) => {
              const changerName = version.changedBy
                ? [version.changedBy.firstName, version.changedBy.lastName]
                  .filter(Boolean)
                  .join(' ') || version.changedBy.email
                : 'Unknown'
              const toSnapshot = index === 0
                ? currentProperty
                : sortedVersions[index - 1].snapshot
              const fieldChanges = computePropertyDiff(version.snapshot, toSnapshot)
              return (
                <div
                  key={version.id}
                  className="rounded-lg border border-border p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">
                          v{version.versionNumber}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(version.changedAt)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{changerName}</p>
                      {version.changeSummary && (
                        <p className="text-xs text-foreground mt-1">{version.changeSummary}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingRestoreVersion(version.versionNumber)}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                      Restore
                    </Button>
                  </div>
                  {fieldChanges.length > 0 && (
                    <dl className="mt-2 pt-2 border-t border-border/50">
                      {fieldChanges.map((change) => (
                        <FieldDiffRow
                          key={change.field}
                          label={change.label}
                          from={change.from}
                          to={change.to}
                          longText={change.longText}
                        />
                      ))}
                    </dl>
                  )}
                  {fieldChanges.length === 0 && (
                    <p className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground">
                      No tracked field changes detected.
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <AlertDialog
        open={pendingRestoreVersion !== null}
        onOpenChange={(open) => { if (!open) setPendingRestoreVersion(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={RotateCcw}
              iconColor="amber"
              title={`Restore to v${pendingRestoreVersion}?`}
              description={
                'This will record a new version snapshotting the current state' +
                ' and then restore to the selected version.'
              }
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring} onClick={() => setPendingRestoreVersion(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction disabled={restoring} onClick={handleRestore}>
              {restoring ? 'Restoring…' : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface EditorBodyProps {
  property?: MarketplaceProperty
  form: ReturnType<typeof useForm<EditorFormValues>>
  images: string[]
  onImagesChange: (images: string[]) => void
  createMode?: boolean
}

const EditorBody: FC<EditorBodyProps> = ({ property, form, images, onImagesChange, createMode }) => {
  const { register, watch, control, formState: { errors } } = form
  const { fields: subPropertyFields, append: appendSubProperty, remove: removeSubProperty } = useFieldArray({
    control,
    name: 'subProperties',
  })
  const watchedValues = watch()
  const watchedSubProperties = watch('subProperties')
  const assetClass = watchedValues.dstAssetClass ?? property?.dstAssetClass ?? 'other'
  const returnMin = watchedValues.dstExpectedReturnMin
    ? Number(watchedValues.dstExpectedReturnMin) : (property?.dstExpectedReturnMin ?? null)
  const returnMax = watchedValues.dstExpectedReturnMax
    ? Number(watchedValues.dstExpectedReturnMax) : (property?.dstExpectedReturnMax ?? null)
  const holdMonths = watchedValues.dstHoldPeriodMonths
    ? Number(watchedValues.dstHoldPeriodMonths) : (property?.dstHoldPeriodMonths ?? null)
  const minInvestCents = parseDollarsAsCents(watchedValues.dstMinInvestmentDollars ?? '')
    ?? (property?.dstMinInvestmentCents ?? null)
  const sponsor = watchedValues.sponsor || property?.sponsor || null

  const SELECT_CLASS = cn(
    'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  )

  return (
    <div className="max-w-6xl mx-auto px-4 pb-16">
      <div className="relative w-full aspect-video bg-muted rounded-2xl overflow-hidden mb-8">
        {images[0] ? (
          <img
            src={images[0]}
            alt={watchedValues.title || property?.title || 'New listing'}
            className="w-full h-full object-cover"
          />
        ) : (
          <SponsorMonogram sponsor={sponsor} />
        )}
        <div className="absolute inset-0 bg-black/40 flex items-end">
          <div className="w-full p-6">
            <p className="text-white/70 text-sm mb-1">Hero image</p>
            <p className="text-white text-base font-medium">
              {images.length} image{images.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <Tabs defaultValue="property">
            <TabsList className="mb-6 overflow-x-auto flex-nowrap">
              <TabsTrigger value="property">Property</TabsTrigger>
              <TabsTrigger value="sponsor">Sponsor</TabsTrigger>
              <TabsTrigger value="returns">Returns</TabsTrigger>
              <TabsTrigger value="risks">Risks</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
            </TabsList>

            <TabsContent value="property">
              <div className="space-y-5">
                {createMode && (
                  <EditableField label="Org ID">
                    <Input
                      {...register('orgId')}
                      placeholder="org_01abc..."
                      className={cn(
                        'mt-1',
                        errors.orgId ? 'border-destructive' : ''
                      )}
                      aria-label="Org ID"
                    />
                    {errors.orgId && (
                      <p className="text-xs text-destructive mt-1">{errors.orgId.message}</p>
                    )}
                  </EditableField>
                )}
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Controller
                    name="qualifiedStatus"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? 'pending_review'}
                        onValueChange={field.onChange}
                        className="flex gap-2"
                      >
                        {[
                          { value: 'qualified', label: 'Qualified DST', className: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border-emerald-300' },
                          { value: 'pending_review', label: 'Pending Review', className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-300' },
                          { value: 'unqualified', label: 'Unqualified', className: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 border-rose-300' },
                        ].map((option) => (
                          <label
                            key={option.value}
                            htmlFor={`qual-${option.value}`}
                            className={cn(
                              'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold cursor-pointer',
                              'border transition-all duration-150',
                              field.value === option.value
                                ? option.className
                                : 'border-border bg-muted text-muted-foreground hover:border-primary'
                            )}
                          >
                            <RadioGroupItem value={option.value} id={`qual-${option.value}`} className="sr-only" />
                            {option.value === 'qualified' && (
                              <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                            {option.label}
                          </label>
                        ))}
                      </RadioGroup>
                    )}
                  />
                </div>

                <EditableField label="Title">
                  <Input
                    {...register('title')}
                    placeholder="Property title"
                    className={cn(
                      'text-2xl font-bold h-auto py-2 border-transparent bg-transparent',
                      'hover:border-input focus:border-input focus:bg-background transition-colors',
                      errors.title ? 'border-destructive' : ''
                    )}
                    aria-label="Listing title"
                  />
                  {errors.title && (
                    <p className="text-xs text-destructive mt-1">{errors.title.message}</p>
                  )}
                </EditableField>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField label="Sponsor">
                    <Input
                      {...register('sponsor')}
                      placeholder="Sponsor name"
                      className="mt-1"
                      aria-label="Sponsor"
                    />
                  </EditableField>
                  <EditableField label="Asset Class">
                    <select
                      {...register('dstAssetClass')}
                      aria-label="Asset class"
                      className={cn(SELECT_CLASS)}
                    >
                      <option value="">Select asset class</option>
                      {DST_ASSET_CLASSES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    {assetClass && assetClass !== '' && (
                      <span className={cn(
                        'mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize',
                        assetClassBadgeClass(assetClass)
                      )}>
                        {assetClass}
                      </span>
                    )}
                  </EditableField>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField label="Property Type">
                    <select
                      {...register('propertyType')}
                      aria-label="Property type"
                      className={cn(SELECT_CLASS)}
                    >
                      <option value="">Select type</option>
                      <option value="dst">DST</option>
                      <option value="tic">TIC</option>
                      <option value="nnn">NNN</option>
                      <option value="other">Other</option>
                    </select>
                  </EditableField>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField label="City">
                    <Input
                      {...register('addressCity')}
                      placeholder="Austin"
                      className="mt-1"
                      aria-label="City"
                    />
                  </EditableField>
                  <EditableField label="State">
                    <Input
                      {...register('addressState')}
                      placeholder="TX"
                      maxLength={2}
                      className="mt-1"
                      aria-label="State"
                    />
                    {errors.addressState && (
                      <p className="text-xs text-destructive mt-1">{errors.addressState.message}</p>
                    )}
                  </EditableField>
                </div>

                <EditableField label="Street Address">
                  <Input
                    {...register('addressStreet')}
                    placeholder="123 Main St"
                    className="mt-1"
                    aria-label="Street address"
                  />
                </EditableField>

                <EditableField label="Description">
                  <Textarea
                    {...register('description')}
                    placeholder="Property description…"
                    rows={5}
                    className="mt-1 leading-relaxed"
                    aria-label="Description"
                  />
                </EditableField>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">Properties in this fund</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => appendSubProperty({ name: '', address: '', value: 0, imageUrl: '' })}
                      className="gap-1.5"
                      aria-label="Add sub-property"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      Add Property
                    </Button>
                  </div>
                  {subPropertyFields.length > 0 && (
                    <div className="space-y-3">
                      {subPropertyFields.map((field, index) => (
                        <div
                          key={field.id}
                          className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end p-3 rounded-lg border border-border bg-muted/30"
                        >
                          <div>
                            <Label className="text-xs text-muted-foreground">Name</Label>
                            <Input
                              {...register(`subProperties.${index}.name`)}
                              placeholder="Property name"
                              className="mt-1"
                              aria-label={`Sub-property ${index + 1} name`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Address</Label>
                            <Input
                              {...register(`subProperties.${index}.address`)}
                              placeholder="City, ST"
                              className="mt-1"
                              aria-label={`Sub-property ${index + 1} address`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Value ($)</Label>
                            <Input
                              {...register(`subProperties.${index}.value`, { valueAsNumber: true })}
                              type="number"
                              placeholder="5000000"
                              className="mt-1"
                              aria-label={`Sub-property ${index + 1} value`}
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label className="text-xs text-muted-foreground">Image URL</Label>
                              <Input
                                {...register(`subProperties.${index}.imageUrl`)}
                                placeholder="https://..."
                                className="mt-1"
                                aria-label={`Sub-property ${index + 1} image URL`}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSubProperty(index)}
                              aria-label={`Remove sub-property ${index + 1}`}
                              className="text-muted-foreground hover:text-destructive mb-0.5"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {(() => {
                        const subTotal = watchedSubProperties.reduce(
                          (sum: number, subProperty: { value: number }) => sum + (Number(subProperty.value) || 0), 0
                        )
                        const fundSize = parseDollarsAsCents(watchedValues.dstOfferingSizeDollars ?? '')
                        const fundSizeDollars = fundSize != null ? fundSize / 100 : null
                        const delta = fundSizeDollars != null && subTotal > 0
                          ? Math.abs(subTotal - fundSizeDollars) / fundSizeDollars
                          : null
                        const warnColor = delta != null && delta > 0.02
                        return (
                          <p className={cn(
                            'text-xs mt-1',
                            warnColor ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
                          )}>
                            Total: ${subTotal.toLocaleString()}
                            {fundSizeDollars != null && (
                              <span> — Fund size: ${fundSizeDollars.toLocaleString()}</span>
                            )}
                            {warnColor && <span> (delta &gt;2%)</span>}
                          </p>
                        )
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="sponsor">
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                    <SponsorMonogram sponsor={sponsor} textClassName="text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {sponsor ? `About ${sponsor}` : 'About this Sponsor'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Sponsor monogram auto-generated from name
                    </p>
                  </div>
                </div>
                <EditableField label="Sponsor Name">
                  <Input
                    {...register('sponsor')}
                    placeholder="Sponsor name"
                    className="mt-1"
                    aria-label="Sponsor name"
                  />
                </EditableField>
                <Card>
                  <CardContent className="pt-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Full sponsor profile coming soon — contact your advisor for due diligence materials,
                      track record, and AUM details.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="returns">
              <div className="space-y-5">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Target Return (Preview)</p>
                  <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                    {formatReturnRange(returnMin, returnMax)}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField
                    label="Expected Return Min (%)"
                    preview={watchedValues.dstExpectedReturnMin ? `${watchedValues.dstExpectedReturnMin}% min` : undefined}
                  >
                    <Input
                      {...register('dstExpectedReturnMin')}
                      type="number"
                      step="0.1"
                      placeholder="6.5"
                      className="mt-1"
                      aria-label="Expected return minimum"
                    />
                  </EditableField>
                  <EditableField
                    label="Expected Return Max (%)"
                    preview={watchedValues.dstExpectedReturnMax ? `${watchedValues.dstExpectedReturnMax}% max` : undefined}
                  >
                    <Input
                      {...register('dstExpectedReturnMax')}
                      type="number"
                      step="0.1"
                      placeholder="8.0"
                      className="mt-1"
                      aria-label="Expected return maximum"
                    />
                  </EditableField>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField
                    label="Minimum Investment (USD)"
                    preview={minInvestCents != null ? `= ${formatCentsCompact(minInvestCents)}` : undefined}
                  >
                    <Input
                      {...register('dstMinInvestmentDollars')}
                      placeholder="25000"
                      className="mt-1"
                      aria-label="Minimum investment"
                    />
                  </EditableField>
                  <EditableField
                    label="Hold Period (months)"
                    preview={holdMonths != null ? `= ${formatHoldPeriod(holdMonths)}` : undefined}
                  >
                    <Input
                      {...register('dstHoldPeriodMonths')}
                      type="number"
                      placeholder="84"
                      className="mt-1"
                      aria-label="Hold period months"
                    />
                  </EditableField>
                </div>
                <EditableField label="Distribution Frequency">
                  <Controller
                    name="dstDistributionFrequency"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? ''}
                        onValueChange={(v) =>
                          field.onChange(v as 'monthly' | 'quarterly' | 'annually')
                        }
                        className="flex gap-4 mt-2"
                      >
                        {DISTRIBUTION_FREQUENCIES.map((f) => (
                          <div key={f.value} className="flex items-center gap-2">
                            <RadioGroupItem value={f.value} id={`freq-${f.value}`} />
                            <Label htmlFor={`freq-${f.value}`}>{f.label}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                  />
                  {watchedValues.dstDistributionFrequency && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistributionFrequency(watchedValues.dstDistributionFrequency)} distributions
                    </p>
                  )}
                </EditableField>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField label="Offering Size (USD)">
                    <Input
                      {...register('dstOfferingSizeDollars')}
                      placeholder="50000000"
                      className="mt-1"
                      aria-label="Offering size"
                    />
                  </EditableField>
                  <EditableField label="Asking Price (USD)">
                    <Input
                      {...register('askingPriceDollars')}
                      placeholder="1000000"
                      className="mt-1"
                      aria-label="Asking price"
                    />
                  </EditableField>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <EditableField label="Cap Rate (%)">
                    <Input
                      {...register('capRate')}
                      type="number"
                      step="0.1"
                      placeholder="5.5"
                      className="mt-1"
                      aria-label="Cap rate"
                    />
                  </EditableField>
                  <EditableField label="NOI ($)">
                    <Input
                      {...register('noi')}
                      type="number"
                      placeholder="137500"
                      className="mt-1"
                      aria-label="NOI"
                    />
                  </EditableField>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="risks">
              <div className="space-y-4">
                <div className={cn(
                  'rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20',
                  'px-4 py-3 text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2'
                )}>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  Risk factors are standard disclosures and cannot be edited inline.
                </div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                      DST Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {[
                        'DST interests are illiquid and cannot be easily sold or redeemed prior to the end of the holding period.',
                        'Leverage risk: many DSTs use financing, which can amplify losses and result in loss of principal.',
                        'Master-tenant default risk: the DST sponsor acts as master tenant; default could impact distributions.',
                        'Sponsor concentration risk: performance depends on the capabilities of a single sponsor.',
                        'IRS recharacterization risk: if the exchange is improperly structured, it may not qualify under IRC §1031.',
                      ].map((risk) => (
                        <li key={risk} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="images">
              {createMode ? (
                <div
                  className={cn(
                    'flex flex-col items-center justify-center gap-3 rounded-xl',
                    'border-2 border-dashed border-border p-10 text-center'
                  )}
                  aria-label="Image upload unavailable until listing is saved"
                >
                  <UploadCloud className="h-10 w-10 text-muted-foreground opacity-40" aria-hidden="true" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Save the listing first, then upload images.
                  </p>
                </div>
              ) : (
                <ImageGallery
                  propertyId={property!.id}
                  images={images}
                  onImagesChange={onImagesChange}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>

        <aside className="w-full lg:w-80 flex-shrink-0">
          <div className="sticky top-24 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" aria-hidden="true" />
                  Financial Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <RailStat
                  icon={<TrendingUp className="h-4 w-4" aria-hidden="true" />}
                  label="Target Return"
                  value={formatReturnRange(returnMin, returnMax)}
                  highlight
                />
                <RailStat
                  icon={<DollarSign className="h-4 w-4" aria-hidden="true" />}
                  label="Min Investment"
                  value={formatCentsCompact(minInvestCents)}
                />
                <RailStat
                  icon={<Clock className="h-4 w-4" aria-hidden="true" />}
                  label="Hold Period"
                  value={formatHoldPeriod(holdMonths)}
                />
                {sponsor && (
                  <RailStat
                    icon={<Building2 className="h-4 w-4" aria-hidden="true" />}
                    label="Sponsor"
                    value={sponsor}
                  />
                )}
                {assetClass && assetClass !== '' && (
                  <RailStat
                    icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />}
                    label="Asset Class"
                    value={assetClass}
                  />
                )}
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                    {[watchedValues.addressCity || property?.addressCity, watchedValues.addressState || property?.addressState]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                  Offering Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Document management via the investor portal. Upload to storage and link here in a future release.
                </p>
              </CardContent>
            </Card>

            <div className="text-xs text-muted-foreground leading-relaxed px-1">
              Distribution frequency:{' '}
              <span className="font-medium text-foreground">
                {formatDistributionFrequency(watchedValues.dstDistributionFrequency ?? null)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed px-1">
              <Calendar className="h-3 w-3 inline mr-1" aria-hidden="true" />
              Offering size:{' '}
              <span className="font-medium text-foreground">
                {formatCentsCompact(
                  parseDollarsAsCents(watchedValues.dstOfferingSizeDollars ?? '') ?? (property?.dstOfferingSizeCents ?? null)
                )}
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export const AdminMarketplaceEditor: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const mode = id ? 'edit' : 'create'

  const [images, setImages] = useState<string[]>([])
  const [published, setPublished] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [scrolledPastHero, setScrolledPastHero] = useState(false)

  const { data: property, status, error } = useQuery({
    queryKey: ['admin-marketplace-property', id],
    queryFn: () => adminApi.getMarketplaceProperty(id!),
    enabled: mode === 'edit' && !!id,
  })

  const form = useForm<EditorFormValues>({
    resolver: zodResolver(editorSchema),
    defaultValues: {
      title: '',
      orgId: '',
      description: '',
      propertyType: 'dst',
      addressStreet: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
      askingPriceDollars: '',
      sponsor: '',
      dstAssetClass: '',
      dstMinInvestmentDollars: '',
      dstExpectedReturnMin: '',
      dstExpectedReturnMax: '',
      dstHoldPeriodMonths: '',
      dstDistributionFrequency: undefined,
      dstOfferingSizeDollars: '',
      qualifiedStatus: 'pending_review',
      capRate: '',
      noi: '',
      subProperties: [],
    },
  })

  const { reset, watch, formState: { isDirty } } = form
  const watchedTitle = watch('title')

  useEffect(() => {
    if (!property) return
    const savedDraft = id ? localStorage.getItem(storageKey(id)) : null
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft) as { values: EditorFormValues; images: string[] }
        reset(parsed.values)
        setImages(parsed.images)
      } catch {
        reset(propertyToFormValues(property))
        setImages(property.images)
      }
    } else {
      reset(propertyToFormValues(property))
      setImages(property.images)
    }
    setPublished(property.published)
    setLastSavedAt(new Date(property.updatedAt))
  }, [property, id, reset])

  const watchedValues = watch()

  useEffect(() => {
    if (!id || !isDirty) return
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      localStorage.setItem(storageKey(id), JSON.stringify({ values: watchedValues, images }))
    }, AUTOSAVE_DEBOUNCE_MS)
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
  }, [watchedValues, images, id, isDirty])

  useEffect(() => {
    const handleScroll = () => {
      setScrolledPastHero(window.scrollY > 200)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const createMutation = useMutation({
    mutationFn: async (payload: CreateMarketplacePropertyData) => {
      return adminApi.createMarketplaceProperty(payload)
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
      toast.success('Listing created')
      navigate(`/app/admin/marketplace/${created.id}/edit`)
    },
    onError: () => {
      toast.error('Failed to create listing')
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (payload: Partial<CreateMarketplacePropertyData>) => {
      if (!id) throw new Error('No property ID')
      const { orgId: _orgId, published: _published, ...rest } = payload
      const patch = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v !== undefined && v !== '')
      ) as Partial<CreateMarketplacePropertyData>
      return adminApi.updateMarketplaceProperty(id, patch)
    },
    onSuccess: (saved) => {
      setLastSavedAt(new Date())
      if (id) localStorage.removeItem(storageKey(id))
      reset(propertyToFormValues(saved))
      setImages(saved.images)
      setPublished(saved.published)
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-property', id] })
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
      toast.success('Listing saved')
    },
    onError: () => {
      toast.error('Failed to save listing')
    },
  })

  const publishMutation = useMutation({
    mutationFn: async (nextPublished: boolean) => {
      if (!id) throw new Error('No property ID')
      return adminApi.toggleMarketplacePropertyActive(id, nextPublished)
    },
    onSuccess: (saved) => {
      setPublished(saved.published)
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-property', id] })
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
      toast.success(saved.published ? 'Listing published' : 'Listing unpublished')
    },
    onError: () => {
      toast.error('Failed to update publish status')
    },
  })

  const handleSave = form.handleSubmit((values) => {
    if (mode === 'create') {
      const payload = buildPayload(values, images, false) as CreateMarketplacePropertyData
      createMutation.mutate(payload)
      return
    }
    if (!property) return
    const payload = buildPayload(values, images, published)
    saveMutation.mutate(payload)
  })

  const handleDiscard = () => {
    if (!property) return
    if (id) localStorage.removeItem(storageKey(id))
    reset(propertyToFormValues(property))
    setImages(property.images)
    setPublished(property.published)
    setDiscardConfirmOpen(false)
    toast.success('Changes discarded')
  }

  const handleVersionRestored = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-marketplace-property', id] })
    queryClient.invalidateQueries({ queryKey: ['admin-marketplace-properties'] })
  }

  if (mode === 'edit' && status === 'pending') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (mode === 'edit' && status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-muted-foreground">
        <AlertCircle className="h-16 w-16 text-destructive" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-foreground">
          {(error as { status?: number })?.status === 404 ? 'Listing not found' : 'Unable to load listing'}
        </h2>
        <Button onClick={() => navigate('/app/admin/marketplace')}>
          <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
          Back to Marketplace
        </Button>
      </div>
    )
  }

  if (mode === 'edit' && !property) return null

  const mutating = createMutation.isPending || saveMutation.isPending || publishMutation.isPending

  return (
    <div className="min-h-screen bg-background">
      <header
        className={cn(
          'sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border',
          'transition-shadow duration-200',
          scrolledPastHero ? 'shadow-sm' : ''
        )}
        role="banner"
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app/admin/marketplace')}
            className="gap-2 flex-shrink-0"
            aria-label="Back to marketplace"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Back</span>
          </Button>

          <div className="flex-1 min-w-0 text-center">
            <h1 className="text-base font-semibold text-foreground truncate leading-tight">
              {mode === 'create'
                ? (watchedTitle || 'New listing')
                : (watchedTitle || property?.title || 'Untitled Listing')}
            </h1>
            {mode === 'edit' && (
              <div className="flex items-center justify-center gap-2 mt-0.5">
                <button
                  type="button"
                  onClick={() => publishMutation.mutate(!published)}
                  disabled={mutating}
                  aria-label={`Toggle publish status, currently ${published ? 'published' : 'draft'}`}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    'border cursor-pointer transition-all hover:opacity-80 disabled:opacity-50',
                    published
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 border-emerald-300'
                      : 'bg-secondary text-muted-foreground border-border'
                  )}
                >
                  {publishMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : published ? (
                    <CheckCircle className="h-3 w-3" aria-hidden="true" />
                  ) : null}
                  {published ? 'Published' : 'Draft'}
                </button>
                <span className="text-xs text-muted-foreground">
                  {isDirty
                    ? 'Unsaved changes'
                    : lastSavedAt ? `Saved ${formatRelativeTime(lastSavedAt.toISOString())}` : ''}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {mode === 'edit' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryOpen(true)}
                className="gap-1.5"
                aria-label="Version history"
              >
                <History className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">History</span>
              </Button>
            )}
            {mode === 'edit' && (
              <button
                type="button"
                onClick={() => setDiscardConfirmOpen(true)}
                disabled={!isDirty || mutating}
                className={cn(
                  'text-sm text-muted-foreground hover:text-foreground transition-colors',
                  'disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1 rounded'
                )}
                aria-label="Discard changes"
              >
                Discard
              </button>
            )}
            <Button
              onClick={handleSave}
              disabled={mutating}
              size="sm"
              className="gap-2"
              aria-label={mode === 'create' ? 'Create listing' : 'Save listing'}
            >
              {(createMutation.isPending || saveMutation.isPending) ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-4 w-4" aria-hidden="true" />
              )}
              {mode === 'create' ? 'Create listing' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      <main>
        <EditorBody
          property={property}
          form={form}
          images={images}
          onImagesChange={setImages}
          createMode={mode === 'create'}
        />
        <ComplianceFooter />
      </main>

      {mode === 'edit' && property && (
        <VersionHistorySheet
          propertyId={property.id}
          currentProperty={property}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onRestored={handleVersionRestored}
        />
      )}
      {mode === 'edit' && historyOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          aria-hidden="true"
          onClick={() => setHistoryOpen(false)}
        />
      )}

      <AlertDialog
        open={discardConfirmOpen}
        onOpenChange={(open) => { if (!open) setDiscardConfirmOpen(false) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <DialogHeaderWithIcon
              icon={RotateCcw}
              iconColor="amber"
              title="Discard changes?"
              description="All unsaved changes will be lost. This cannot be undone."
              variant="alertDialog"
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDiscardConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
